import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { store } from '../../../redux_setup/store';
import {
  setIsDragging,
  setDragSource,
  setDraggedItem,
  setIsCloneDragging,
  setCalendarItems,
  updateCalendarItemsOrder,
  setPlaceholderInfo,
  setDragIsInProgress,
  setDroppableContainerCurrentlyHoveredByDraggedItem,
  moveExerciseBetweenDatesMultiEnabled,
  setOnBeforeCapture,
  setActiveModal,
  setSelectedSavedWorkout,
  setSelectedDate,
} from '../ducks/calendarActions';
import {
  getSessionBreakChunk,
  isIndexInsideCollapsedSessionBreak,
  getIndexAfterSessionBreakGroup,
  wasUnderCollapsedSessionBreak,
} from './dragSessionBreakHelpers';

const MONTH_DAY_PREFIX = 'month-day-';

const isMonthDay = (id) => typeof id === 'string' && id.startsWith(MONTH_DAY_PREFIX);
const dateFromMonthDayId = (id) => id.slice(MONTH_DAY_PREFIX.length);

const getDayItems = (state, dateKey) => state.calendar.data.calendarItems[dateKey]?.exercises || [];

/**
 * Build a synthetic `expandedItems` map where every session_break in the
 * provided item arrays is marked expanded (true). Month view renders all
 * items unconditionally — there's no collapsed/expanded UI here — so the
 * drop helpers (`isIndexInsideCollapsedSessionBreak`,
 * `wasUnderCollapsedSessionBreak`) should behave as if every break is
 * expanded. Feeding them this map gives us week-view-style expanded drop
 * behavior (drop into the middle of a break group, no collapsed-gap
 * redirect, no scoop-prevention false positives) without touching redux,
 * so the user's actual expand/collapse choices in week view aren't
 * altered when they jump back.
 */
const getExpandedMapForMonthView = (...itemArrays) => {
  const map = {};
  itemArrays.forEach((arr) => {
    if (!arr) return;
    arr.forEach((item) => {
      if (item?.type === 'session_break' && item.assignedId !== undefined) {
        map[item.assignedId] = true;
      }
    });
  });
  return map;
};

/**
 * Drag manager for the calendar month view. Lives alongside (not inside)
 * `useDragManager` so day/week behavior stays untouched. Scope:
 *  - same-day reorder within a month-day cell
 *  - cross-day move between two month-day cells
 *  - session break chunk movement (single chunk only)
 *
 * Sidebar → month-day drop is intentionally NOT supported: the sidebar
 * trigger is hidden in month view (see IconSideBar.js gating on
 * `viewType === 'monthView'`), so the source-id list never appears here.
 * preSave items added in week view do reach this manager via the in-grid
 * paths and are skipped from the persistence calls below.
 *
 * Out of scope: combine/superset, multi-select, free-form-program.
 */
const useMonthViewDragManager = () => {
  const dispatch = useDispatch();

  const cleanupDragState = useCallback(() => {
    dispatch(setDragIsInProgress(false));
    dispatch(setPlaceholderInfo(null));
    dispatch(setDroppableContainerCurrentlyHoveredByDraggedItem(null));
    dispatch(setOnBeforeCapture(null));
    dispatch(setIsCloneDragging(false));
    dispatch(setIsDragging(false));
    dispatch(setDragSource(null));
    dispatch(setDraggedItem(null));
  }, [dispatch]);

  const handleSameDayReorder = useCallback((result, accountCode) => {
    const { source, destination } = result;
    const dateKey = dateFromMonthDayId(source.droppableId);
    const state = store.getState();
    const originalItems = Array.from(getDayItems(state, dateKey));
    const expandedItems = getExpandedMapForMonthView(originalItems);
    const currentItem = originalItems[source.index];
    if (!currentItem) return;

    /** Session-break chunk extraction (mirrors useDragManager same-day path). */
    const sessionBreakChunk = currentItem.type === 'session_break'
      ? getSessionBreakChunk(originalItems, source.index)
      : [];
    const chunk = sessionBreakChunk.length ? sessionBreakChunk : [currentItem];

    const filteredItems = originalItems.filter((item) => !chunk.includes(item));

    let insertIndex = destination.index;
    if (sessionBreakChunk.length && destination.index > source.index) {
      /** rbd's destination.index already accounts for the dragged item
       * being removed, but it doesn't know we're also moving the rest
       * of the session-break chunk along with it. When dropping below
       * the original position, subtract (chunkLength - 1) so the chunk
       * lands where the user expects instead of overshooting by the
       * size of the trailing chunk items. */
      insertIndex = insertIndex - (sessionBreakChunk.length - 1);
    }

    /** Don't drop a single item into the visual gap of a collapsed
     * session break — slide to the end of that group instead. */
    if (chunk.length === 1
      && isIndexInsideCollapsedSessionBreak(filteredItems, insertIndex, expandedItems)) {
      insertIndex = getIndexAfterSessionBreakGroup(filteredItems, insertIndex);
    }

    filteredItems.splice(insertIndex, 0, ...chunk);

    /** Scoop-prevention: after splicing a session-break chunk, we may have
     * pulled in items hidden under a different collapsed break at the
     * destination. Move those above the dragged break. */
    let finalItems = filteredItems;
    if (sessionBreakChunk.length) {
      const newChunkAfterInsert = getSessionBreakChunk(filteredItems, insertIndex);
      const unintentionallyMovedItems = newChunkAfterInsert.filter((newItem) => {
        const wasInOriginal = sessionBreakChunk.some(
          (orig) => orig.assignedId === newItem.assignedId,
        );
        return !wasInOriginal
          && wasUnderCollapsedSessionBreak(newItem, expandedItems, originalItems);
      });

      if (unintentionallyMovedItems.length) {
        const movedIds = unintentionallyMovedItems.map((i) => String(i.assignedId));
        const stripped = filteredItems.filter((i) => !movedIds.includes(String(i.assignedId)));
        const draggedIndex = stripped.findIndex(
          (item) => item.assignedId === currentItem.assignedId,
        );
        if (draggedIndex !== -1) {
          stripped.splice(draggedIndex, 0, ...unintentionallyMovedItems);
        }
        finalItems = stripped;
      }
    }

    /** Always commit the local reorder up front so the UI reflects the
     * drop even when no saved items remain (e.g. a day made up entirely
     * of preSave items dragged in from week view). The persistence call
     * below is what depends on numeric assignedIds. */
    dispatch(setCalendarItems(finalItems, dateKey));

    /** Build ordered assignedIds (preSave items have string ids — exclude). */
    const newOrderedAssignmentIds = finalItems
      .map((item) => item.assignedId)
      .filter((id) => typeof id !== 'string');

    if (!newOrderedAssignmentIds.length) return;

    const filteredAssignedIdsBeingMoved = chunk
      .map((item) => item.assignedId)
      .filter((id) => typeof id !== 'string');

    const submissionObject = {
      date: dateKey,
      order: newOrderedAssignmentIds,
      assignedIdsBeingMoved: filteredAssignedIdsBeingMoved,
      movedToIndex: newOrderedAssignmentIds.indexOf(filteredAssignedIdsBeingMoved[0]),
    };

    dispatch(updateCalendarItemsOrder(accountCode, submissionObject, originalItems));
  }, [dispatch]);

  const handleCrossDayMove = useCallback((result, accountCode) => {
    const { source, destination } = result;
    const sourceDate = dateFromMonthDayId(source.droppableId);
    const destDate = dateFromMonthDayId(destination.droppableId);
    const state = store.getState();

    const startItems = Array.from(getDayItems(state, sourceDate));
    const endItems = Array.from(getDayItems(state, destDate));
    const expandedItems = getExpandedMapForMonthView(startItems, endItems);
    const removed = startItems[source.index];
    if (!removed) return;

    const sessionBreakChunk = removed.type === 'session_break'
      ? getSessionBreakChunk(startItems, source.index)
      : [];

    if (sessionBreakChunk.length > 1) {
      /** Cross-day session break chunk move (mirrors useDragManager
       * cross-date branch line ~929). */
      const newStartItems = startItems.filter(
        (item) => !sessionBreakChunk.some((sb) => sb.assignedId === item.assignedId),
      );
      const newEndItems = [...endItems];
      newEndItems.splice(destination.index, 0, ...sessionBreakChunk);

      const newChunkAfterInsert = getSessionBreakChunk(newEndItems, destination.index);
      const unintentionallyMovedItems = newChunkAfterInsert.filter((newItem) => {
        const wasInOriginal = sessionBreakChunk.some(
          (orig) => orig.assignedId === newItem.assignedId,
        );
        return !wasInOriginal
          && wasUnderCollapsedSessionBreak(newItem, expandedItems, endItems);
      });

      let finalEndItems = newEndItems;
      if (unintentionallyMovedItems.length) {
        const movedIds = unintentionallyMovedItems.map((i) => String(i.assignedId));
        const stripped = newEndItems.filter((i) => !movedIds.includes(String(i.assignedId)));
        const draggedIndex = stripped.findIndex(
          (item) => item.assignedId === removed.assignedId,
        );
        if (draggedIndex !== -1) {
          stripped.splice(draggedIndex, 0, ...unintentionallyMovedItems);
        }
        finalEndItems = stripped;
      }

      dispatch(setCalendarItems(newStartItems, sourceDate));
      dispatch(setCalendarItems(finalEndItems, destDate));

      const filteredAssignedIds = sessionBreakChunk
        .map((i) => i.assignedId)
        .filter((id) => typeof id !== 'string');
      if (!filteredAssignedIds.length) return;

      /** Pass `finalEndItems` (post-scoop-prevention) so the post-move
       * reorder payload computed inside the thunk matches what we just
       * dispatched to redux. Passing `newEndItems` here would persist
       * the un-corrected order and the UI would snap to it on refresh. */
      dispatch(moveExerciseBetweenDatesMultiEnabled(
        accountCode,
        filteredAssignedIds,
        destDate,
        sourceDate,
        destDate,
        finalEndItems,
      )).catch(() => {
        dispatch(setCalendarItems(startItems, sourceDate));
        dispatch(setCalendarItems(endItems, destDate));
      });
      return;
    }

    /** Single-item cross-day move. */
    let adjustedDestinationIndex = destination.index;
    if (isIndexInsideCollapsedSessionBreak(endItems, destination.index, expandedItems)) {
      adjustedDestinationIndex = getIndexAfterSessionBreakGroup(endItems, destination.index);
    }

    const newStartItems = [...startItems];
    newStartItems.splice(source.index, 1);
    const newEndItems = [...endItems];
    newEndItems.splice(adjustedDestinationIndex, 0, removed);

    dispatch(setCalendarItems(newStartItems, sourceDate));
    dispatch(setCalendarItems(newEndItems, destDate));

    /** preSave items (added in week view but not yet saved) live only in
     * redux — skip the network call and let the visual move stand. */
    if (removed.preSave) return;
    if (typeof removed.assignedId === 'string') return;

    dispatch(moveExerciseBetweenDatesMultiEnabled(
      accountCode,
      [removed.assignedId],
      destDate,
      sourceDate,
      destDate,
      newEndItems,
    )).catch(() => {
      dispatch(setCalendarItems(startItems, sourceDate));
      dispatch(setCalendarItems(endItems, destDate));
    });
  }, [dispatch]);

  const handleDragEnd = useCallback((result) => {
    cleanupDragState();

    const { source, destination, combine } = result;
    if (!destination && !combine) return;

    /** Combine/superset is not supported in month view v1. */
    if (combine) return;

    const state = store.getState();
    const { programType } = state.calendar.data;

    /** Free-form-program month view drag is not supported in v1. */
    if (programType === 'free-form-program') return;

    /** Program template dragged from the sidebar onto a month-day cell:
     * open the Load Programs modal with the drop-target date pre-filled
     * as the start date, matching day/week view's behavior. */
    if (source.droppableId === 'templates-sidebar') {
      if (!isMonthDay(destination.droppableId)) return;
      const dateKey = dateFromMonthDayId(destination.droppableId);
      const { savedWorkouts } = state.calendar.data;
      const workoutId = result.draggableId.replace('-sideBar', '');
      const draggedWorkout = savedWorkouts?.find((w) => w.id === parseInt(workoutId, 10));
      dispatch(setSelectedDate(moment(dateKey).toDate()));
      dispatch(setSelectedSavedWorkout(draggedWorkout));
      dispatch(setActiveModal('loadProgramsModal'));
      return;
    }

    /** Source and destination must both be month-day cells. Anything
     * else (e.g., week-day, programmedCalendarItems) means an event
     * leaked across views — bail. */
    if (!isMonthDay(source.droppableId)) return;
    if (!isMonthDay(destination.droppableId)) return;

    const accountCode = state.auth.data.currentUser?.accountCode;
    if (!accountCode) return;

    if (source.droppableId === destination.droppableId) {
      if (source.index === destination.index) return;
      handleSameDayReorder(result, accountCode);
      return;
    }

    handleCrossDayMove(result, accountCode);
  }, [cleanupDragState, handleSameDayReorder, handleCrossDayMove]);

  const handleDragStart = useCallback((start) => {
    const { source, draggableId } = start;
    dispatch(setIsDragging(true));
    dispatch(setDragSource(source.droppableId));

    if (!isMonthDay(source.droppableId)) return;

    /** Resolve dragged item so any UI listening to setDraggedItem
     * (e.g. dragLayer indicators) has the data. */
    const state = store.getState();
    const dateKey = dateFromMonthDayId(source.droppableId);
    const items = getDayItems(state, dateKey);
    const draggedItem = items.find((i) => String(i.assignedId) === draggableId)
      || items[source.index];
    if (draggedItem) {
      dispatch(setDraggedItem({ ...draggedItem, sourceDroppableId: source.droppableId }));
    }
  }, [dispatch]);

  const handleOnBeforeCapture = useCallback(() => {
    /** Month view is a static grid (no virtualization, no placeholder
     * height measurement). Mark drag-in-progress so any cross-cutting
     * UI hooks that listen to it stay consistent with day/week. */
    dispatch(setDragIsInProgress(true));
  }, [dispatch]);

  return {
    handleDragEnd,
    handleDragStart,
    handleOnBeforeCapture,
  };
};

export default useMonthViewDragManager;
